// Marketplace Administration Custom Hook
// Feature: 006-marketplace-management
// Updated: 007-cloudflare-migration - migrated fraud detection to API Gateway

import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import type {
  MarketplaceItemSummary,
  MarketplaceItemWithSeller,
  MarketplaceItemStatus,
  MarketplaceItemUpdate,
  CategoryOption,
} from '../types/marketplaceAdmin';
import type {
  ApiFraudStats,
  ApiFraudFlag,
  ApiFraudBlock,
  FraudConfig,
  ReviewAction,
} from '../services/api/types';
import * as marketplaceService from '../services/marketplaceAdmin';
import { isApiError, ApiErrorCode } from '../services/api/errors';

/**
 * Default page size for pagination
 */
const DEFAULT_PAGE_SIZE = 50;

/**
 * Debounce delay for search input (ms)
 */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * Get localized error message from API error
 */
function getErrorMessage(
  error: unknown,
  t: (key: string) => string,
  fallbackKey: string
): string {
  if (isApiError(error)) {
    const errorKey = `api.errors.${error.code}`;
    const translated = t(errorKey);
    if (translated !== errorKey) {
      return translated;
    }
    return error.message;
  }
  return t(fallbackKey);
}

export function useMarketplaceAdministration() {
  const { t } = useTranslation();

  // State
  // NOTE: Category enrichment pattern - same as useBusinessAdministration.ts
  // Backend returns category_name: null, so we enrich client-side using categories array.
  // rawItems stores API response, items stores enriched data for display.
  const [items, setItems] = useState<MarketplaceItemSummary[]>([]);
  const [rawItems, setRawItems] = useState<MarketplaceItemSummary[]>([]); // Before category enrichment
  const [selectedItem, setSelectedItem] = useState<MarketplaceItemWithSeller | null>(null);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [searchTerm, setSearchTermInternal] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<MarketplaceItemStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fraud detection state (from API Gateway)
  const [fraudStats, setFraudStats] = useState<ApiFraudStats | null>(null);
  const [flaggedItems, setFlaggedItems] = useState<ApiFraudFlag[]>([]);
  const [blockedEntities, setBlockedEntities] = useState<ApiFraudBlock[]>([]);
  const [fraudConfig, setFraudConfig] = useState<FraudConfig | null>(null);
  const [isFraudLoading, setIsFraudLoading] = useState(false);
  const [showFraudDashboard, setShowFraudDashboard] = useState(false);

  // Retry state for exponential backoff
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  // Debounce timer ref
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search term setter
  const setSearchTerm = useCallback((term: string) => {
    setSearchTermInternal(term);
    
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // Set new debounce timer
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchTerm(term);
    }, SEARCH_DEBOUNCE_MS);
  }, []);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Load items list with retry (uses debounced search term)
  const loadItems = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await marketplaceService.getAllMarketplaceItems(
        debouncedSearchTerm,
        statusFilter,
        categoryFilter,
        currentPage,
        DEFAULT_PAGE_SIZE
      );

      // Store raw items - will be enriched with category names in separate useEffect
      setRawItems(response.items);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
      retryCountRef.current = 0; // Reset retry count on success
    } catch (err) {
      const errorMessage = getErrorMessage(err, t, 'admin.marketplace.loadError');
      setError(errorMessage);

      // Retry with exponential backoff for network/server errors
      if (isApiError(err) && 
          (err.code === ApiErrorCode.NETWORK_ERROR || err.code === ApiErrorCode.SERVER_ERROR) &&
          retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        const delay = Math.pow(2, retryCountRef.current) * 1000;
        
        Sentry.addBreadcrumb({
          category: 'marketplace-admin',
          message: `Retrying loadItems (attempt ${retryCountRef.current})`,
          level: 'info',
          data: { delay },
        });

        setTimeout(() => {
          loadItems();
        }, delay);
      }
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearchTerm, statusFilter, categoryFilter, currentPage, t]);

  // Load categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      const cats = await marketplaceService.getMarketplaceCategories();
      setCategories(cats);
    };
    fetchCategories();
  }, []);

  // Load items when filters change
  useEffect(() => {
    loadItems();
  }, [loadItems]);

  // Enrich items with category names from loaded categories
  // NOTE: Same pattern as useBusinessAdministration.ts - backend returns category_name: null
  // so we look up category name client-side using the categoryId field
  useEffect(() => {
    if (rawItems.length === 0) {
      setItems([]);
      return;
    }

    const enriched = rawItems.map(item => {
      // item.categoryId contains UUID from API, look up name from categories
      if (categories.length > 0 && item.categoryId) {
        const matchedCategory = categories.find(cat => cat.id === item.categoryId);
        if (matchedCategory) {
          return { ...item, category: matchedCategory.name };
        }
      }
      return item;
    });

    setItems(enriched);
  }, [rawItems, categories]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, categoryFilter]);

  // Select an item and load its details
  const selectItem = useCallback(async (itemId: string) => {
    setIsLoadingDetails(true);
    setError(null);
    setIsEditing(false);

    try {
      const details = await marketplaceService.getMarketplaceItemDetails(itemId);
      if (!details) {
        setError(t('admin.marketplace.notFound'));
        return;
      }

      setSelectedItem(details);
    } catch (err) {
      const errorMessage = getErrorMessage(err, t, 'admin.marketplace.loadDetailsError');
      setError(errorMessage);
    } finally {
      setIsLoadingDetails(false);
    }
  }, [t]);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedItem(null);
    setIsEditing(false);
  }, []);

  // Change item status
  const changeStatus = useCallback(
    async (newStatus: MarketplaceItemStatus, reason?: string) => {
      if (!selectedItem) {
        return { success: false, error: t('admin.marketplace.noItemSelected') };
      }

      try {
        const result = await marketplaceService.changeItemStatus(
          selectedItem.id,
          newStatus,
          reason
        );

        if (result.success) {
          // Refresh item details and list
          await selectItem(selectedItem.id);
          await loadItems();
        } else if (result.error) {
          setError(result.error);
        }

        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'admin.marketplace.statusChangeError');
        setError(errorMessage);
        return { success: false, error: errorMessage };
      }
    },
    [selectedItem, selectItem, loadItems, t]
  );

  // Delete item (requires reason)
  const deleteItem = useCallback(
    async (reason: string) => changeStatus('deleted', reason),
    [changeStatus]
  );

  // Restore deleted item
  const restoreItem = useCallback(
    async () => changeStatus('active'),
    [changeStatus]
  );

  // Expire item
  const expireItem = useCallback(
    async () => changeStatus('expired'),
    [changeStatus]
  );

  // Reactivate expired item
  const reactivateItem = useCallback(
    async () => changeStatus('active'),
    [changeStatus]
  );

  // Start editing
  const startEditing = useCallback(() => {
    setIsEditing(true);
  }, []);

  // Cancel editing
  const cancelEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  // Save item profile edits with photos
  const saveItemChanges = useCallback(
    async (updates: MarketplaceItemUpdate, newPhotos: File[] = [], removedPhotoUrls: string[] = []) => {
      if (!selectedItem) return { success: false };

      try {
        // First save profile updates
        const updateResult = await marketplaceService.updateMarketplaceItem(
          selectedItem.id,
          updates
        );

        if (!updateResult.success) {
          return { success: false, error: updateResult.error };
        }

        // Handle photo removals if any
        if (removedPhotoUrls.length > 0) {
          const removeResult = await marketplaceService.removeItemPhotos(
            selectedItem.id,
            removedPhotoUrls
          );

          if (!removeResult.success) {
            return { success: false, error: removeResult.error || t('admin.marketplace.photoRemoveError') };
          }
        }

        // Then handle photo uploads if any
        if (newPhotos.length > 0) {
          const photoResult = await marketplaceService.addItemPhotos(
            selectedItem.id,
            newPhotos
          );

          if (!photoResult.success) {
            return { success: false, error: photoResult.error || t('admin.marketplace.photoUploadError') };
          }
        }

        // Refresh item details and list
        await selectItem(selectedItem.id);
        await loadItems();
        setIsEditing(false);

        return { success: true };
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'admin.marketplace.saveError');
        return { success: false, error: errorMessage };
      }
    },
    [selectedItem, selectItem, loadItems, t]
  );

  // Add photos to item
  const addPhotos = useCallback(
    async (files: File[]) => {
      if (!selectedItem) return { success: false };

      try {
        const result = await marketplaceService.addItemPhotos(
          selectedItem.id,
          files
        );

        if (result.success) {
          // Refresh item details
          await selectItem(selectedItem.id);
        }

        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'admin.marketplace.photoUploadError');
        return { success: false, error: errorMessage };
      }
    },
    [selectedItem, selectItem, t]
  );

  // Remove photos from item
  const removePhotos = useCallback(
    async (photoUrls: string[]) => {
      if (!selectedItem) return { success: false };

      try {
        const result = await marketplaceService.removeItemPhotos(
          selectedItem.id,
          photoUrls
        );

        if (result.success) {
          // Refresh item details
          await selectItem(selectedItem.id);
        }

        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'admin.marketplace.photoRemoveError');
        return { success: false, error: errorMessage };
      }
    },
    [selectedItem, selectItem, t]
  );

  // Pagination handlers
  const goToPage = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const nextPage = useCallback(() => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [currentPage, totalPages]);

  const previousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  }, [currentPage]);

  // ==========================================================================
  // Fraud Detection Dashboard (via API Gateway)
  // ==========================================================================

  // Load fraud dashboard data
  const loadFraudDashboard = useCallback(async () => {
    setIsFraudLoading(true);
    setError(null);

    try {
      // Load all fraud data in parallel
      const [statsResult, flaggedResult, blockedResult, configResult] = await Promise.all([
        marketplaceService.getFraudStatistics(),
        marketplaceService.getFlaggedItems(),
        marketplaceService.getBlockedEntities(),
        marketplaceService.getFraudConfiguration(),
      ]);

      if (statsResult.success && statsResult.data) {
        setFraudStats(statsResult.data);
      }
      if (flaggedResult.success && flaggedResult.data) {
        setFlaggedItems(flaggedResult.data);
      }
      if (blockedResult.success && blockedResult.data) {
        setBlockedEntities(blockedResult.data);
      }
      if (configResult.success && configResult.data) {
        setFraudConfig(configResult.data);
      }
    } catch (err) {
      const errorMessage = getErrorMessage(err, t, 'admin.marketplace.fraudLoadError');
      setError(errorMessage);
    } finally {
      setIsFraudLoading(false);
    }
  }, [t]);

  // Toggle fraud dashboard view
  const toggleFraudDashboard = useCallback(() => {
    setShowFraudDashboard((prev) => {
      if (!prev) {
        // Loading fraud data when opening dashboard
        loadFraudDashboard();
      }
      return !prev;
    });
  }, [loadFraudDashboard]);

  // Block a user or IP
  const blockUserOrIp = useCallback(
    async (type: 'user' | 'ip', id: string, reason: string, durationHours?: number) => {
      try {
        const result = await marketplaceService.blockUserOrIp(type, id, reason, durationHours);
        if (result.success) {
          // Refresh blocked list
          const blockedResult = await marketplaceService.getBlockedEntities();
          if (blockedResult.success && blockedResult.data) {
            setBlockedEntities(blockedResult.data);
          }
        }
        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'admin.marketplace.blockError');
        return { success: false, error: errorMessage };
      }
    },
    [t]
  );

  // Unblock a user or IP
  const unblockUserOrIp = useCallback(
    async (type: 'user' | 'ip', id: string) => {
      try {
        const result = await marketplaceService.unblockUserOrIp(type, id);
        if (result.success) {
          // Refresh blocked list
          const blockedResult = await marketplaceService.getBlockedEntities();
          if (blockedResult.success && blockedResult.data) {
            setBlockedEntities(blockedResult.data);
          }
        }
        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'admin.marketplace.unblockError');
        return { success: false, error: errorMessage };
      }
    },
    [t]
  );

  // Review a flagged item
  const reviewFlag = useCallback(
    async (flagId: string, action: ReviewAction, notes?: string) => {
      try {
        const result = await marketplaceService.reviewFlaggedItem(flagId, action, notes);
        if (result.success) {
          // Refresh flagged items list
          const flaggedResult = await marketplaceService.getFlaggedItems();
          if (flaggedResult.success && flaggedResult.data) {
            setFlaggedItems(flaggedResult.data);
          }
          // Refresh stats
          const statsResult = await marketplaceService.getFraudStatistics();
          if (statsResult.success && statsResult.data) {
            setFraudStats(statsResult.data);
          }
        }
        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'admin.marketplace.reviewError');
        return { success: false, error: errorMessage };
      }
    },
    [t]
  );

  // Update fraud configuration
  const updateConfig = useCallback(
    async (config: Partial<FraudConfig>) => {
      try {
        const result = await marketplaceService.updateFraudConfiguration(config);
        if (result.success && result.data) {
          setFraudConfig(result.data);
        }
        return result;
      } catch (err) {
        const errorMessage = getErrorMessage(err, t, 'admin.marketplace.configUpdateError');
        return { success: false, error: errorMessage };
      }
    },
    [t]
  );

  return {
    // State
    items,
    selectedItem,
    categories,
    searchTerm,
    statusFilter,
    categoryFilter,
    currentPage,
    totalPages,
    totalCount,
    isLoading,
    isLoadingDetails,
    isEditing,
    error,

    // Fraud detection state
    fraudStats,
    flaggedItems,
    blockedEntities,
    fraudConfig,
    isFraudLoading,
    showFraudDashboard,

    // Actions
    setSearchTerm,
    setStatusFilter,
    setCategoryFilter,
    selectItem,
    clearSelection,
    deleteItem,
    restoreItem,
    expireItem,
    reactivateItem,
    startEditing,
    cancelEditing,
    saveItemChanges,
    addPhotos,
    removePhotos,
    goToPage,
    nextPage,
    previousPage,
    refreshItems: loadItems,

    // Fraud detection actions
    toggleFraudDashboard,
    loadFraudDashboard,
    blockUserOrIp,
    unblockUserOrIp,
    reviewFlag,
    updateFraudConfig: updateConfig,
  };
}
